import { Check, Globe } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const LANGS = [
	{ code: "ar", key: "language.switch_to_arabic" },
	{ code: "ckb", key: "language.switch_to_kurdish" },
	{ code: "en", key: "language.switch_to_english" },
] as const;

function currentCode(lang: string): "ar" | "ckb" | "en" {
	if (lang.startsWith("ckb")) return "ckb";
	if (lang.startsWith("ar")) return "ar";
	return "en";
}

export function LanguageToggle() {
	const { i18n, t } = useTranslation();
	const [open, setOpen] = useState(false);
	const active = currentCode(i18n.language);

	return (
		<div className="relative">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="inline-flex items-center gap-1 rounded-lg border border-border text-foreground hover:bg-muted active:bg-muted dark:active:bg-gray-700 transition-colors size-11 sm:h-9 sm:w-auto sm:px-3 sm:py-1.5 text-sm font-medium justify-center"
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={t(LANGS.find((l) => l.code === active)!.key)}
			>
				<Globe className="size-5 sm:size-4" strokeWidth={2} aria-hidden />
				<span className="hidden sm:inline">
					{t(LANGS.find((l) => l.code === active)!.key)}
				</span>
			</button>

			{open && (
				<>
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop; close is reachable via the trigger button (toggles open) and the menu items */}
					<div
						className="fixed inset-0 z-20"
						onClick={() => setOpen(false)}
						aria-hidden
					/>
					<div
						role="menu"
						className="absolute z-30 mt-1 end-0 w-36 rounded-xl border border-border bg-card shadow-elevated py-1 animate-pop-in"
					>
						{LANGS.map((l) => (
							<button
								key={l.code}
								type="button"
								role="menuitem"
								onClick={() => {
									i18n.changeLanguage(l.code);
									setOpen(false);
								}}
								className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-start hover:bg-muted ${l.code === active ? "text-brand-700 dark:text-brand-300 font-medium" : "text-foreground"}`}
							>
								<span className="flex size-4 items-center justify-center shrink-0">
									{l.code === active && (
										<Check className="size-4" aria-hidden />
									)}
								</span>
								{t(l.key)}
							</button>
						))}
					</div>
				</>
			)}
		</div>
	);
}
